/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import LegalPageLayout from "./LegalPageLayout";

export default function PrivacyPage() {
  return (
    <LegalPageLayout
      testid="page-privacy"
      eyebrow="— Legal"
      title="Privacy Policy"
      subtitle="How the DONiTSH!T wellness ecosystem collects, uses, and protects your personal information."
      lastUpdated="August 2026"
    >
      <p>
        DONiTSH!T is a premium multilingual wellness ecosystem operating
        educational content, an AI wellness assistant ("Ask Donny"), a
        Dosha Test, a wellness marketplace, a shop, and companion mobile
        applications. This Privacy Policy explains what personal
        information we collect through our website (the "Service"), how
        we use it, and the choices you have.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>a) Account information</h3>
      <ul>
        <li>Email address</li>
        <li>Password (stored as a one-way bcrypt hash — never in plain text)</li>
        <li>Optional full name</li>
        <li>
          If you sign in with Google, we receive your email and profile
          name from Google
        </li>
      </ul>

      <h3>b) Preferences and settings</h3>
      <ul>
        <li>Language selection (one of eleven supported languages)</li>
        <li>
          Country (used to display region-appropriate crisis-line
          resources on our Support page)
        </li>
      </ul>

      <h3>c) Dosha and wellness inputs</h3>
      <ul>
        <li>Answers you submit to the Dosha Test</li>
        <li>Your resulting Dosha profile and personalized recommendations</li>
        <li>Saved Dosha results linked to your account</li>
      </ul>

      <h3>d) Ask Donny conversations</h3>
      <ul>
        <li>
          The text you send to Ask Donny and the responses returned
        </li>
        <li>
          Conversations are associated with your account for continuity
          and to power the retrieval-grounded knowledge base
        </li>
        <li>
          Prompts are sent to OpenAI through Emergent's LLM gateway for
          processing and embedding
        </li>
      </ul>

      <h3>e) Marketplace, offers, and business activity</h3>
      <ul>
        <li>Businesses you save to your favorites</li>
        <li>
          Inquiries and booking requests you submit to marketplace
          listings
        </li>
        <li>
          If you list a business, the listing data you provide (name,
          tagline, description, category, services, languages, city,
          country, contact email, contact phone, website, imagery)
        </li>
        <li>Member-benefit codes you reveal</li>
      </ul>

      <h3>f) Purchases (through third-party checkout)</h3>
      <ul>
        <li>
          Digital and physical purchases are processed through Shopify.
          Payment card details are collected by Shopify — we do not
          store your card information on our servers.
        </li>
        <li>
          A foundational Stripe integration exists in our codebase for
          future paid experiences. No active Stripe billing flow
          processes payments at the time of publication.
        </li>
      </ul>

      <h3>g) Communications</h3>
      <ul>
        <li>
          Messages you send us through the Support contact form (name,
          email, topic, message)
        </li>
        <li>
          Transactional emails we send you (e.g., business-listing
          status updates delivered via Resend)
        </li>
      </ul>

      <h3>h) Technical information</h3>
      <ul>
        <li>
          A functional session cookie (<code>session_token</code>) used
          solely to keep you signed in
        </li>
        <li>Your language and country preferences</li>
      </ul>

      <h3>About Luv Connect and Couples Connect</h3>
      <p>
        On this website, Luv Connect and Couples Connect are educational
        content pages within the Relationships section. The website does
        not currently collect matching, dating, or partner-relationship
        data through Luv Connect or Couples Connect. Any experiences
        that involve user-to-user connection, matching, or messaging
        are provided through the DONiTSH!T mobile applications and are
        governed by their in-app terms.
      </p>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>
          Provide the Service you request (Dosha results, Ask Donny
          answers, marketplace bookings, purchases)
        </li>
        <li>Personalize recommendations across the ecosystem</li>
        <li>
          Communicate transactionally (account emails, booking
          confirmations, business-listing status)
        </li>
        <li>Improve content quality, retrieval, and search</li>
        <li>
          Comply with legal obligations and enforce our{" "}
          <Link to="/terms">Terms of Use</Link>
        </li>
      </ul>

      <h2>3. Legal Bases (EU/UK residents)</h2>
      <p>
        Where the GDPR or UK GDPR applies, we rely on the following
        legal bases: performance of a contract, our legitimate interests,
        your consent (where required), and compliance with legal
        obligations.
      </p>

      <h2>4. Third-Party Service Providers</h2>
      <p>
        We use the following processors to operate the Service. Each is
        bound by its own privacy commitments:
      </p>
      <ul>
        <li>
          <strong>OpenAI</strong> (via the Emergent LLM gateway) — powers
          Ask Donny (GPT models) and knowledge-base embeddings
        </li>
        <li>
          <strong>Google</strong> — Google Sign-In (SSO)
        </li>
        <li>
          <strong>Shopify</strong> — checkout, order fulfillment, and
          payment for products purchased through the DONiTSH!T shop
        </li>
        <li>
          <strong>Resend</strong> — transactional email delivery
        </li>
        <li>
          <strong>MongoDB</strong> — application database
        </li>
        <li>
          <strong>Stripe</strong> — scaffolded for future paid
          experiences; not currently processing payments
        </li>
      </ul>
      <p>
        <strong>We do not sell your personal information.</strong>
      </p>

      <h2>5. International Transfers</h2>
      <p>
        Some processors listed above are located in the United States or
        other jurisdictions outside the EU/UK. Where required, we rely
        on Standard Contractual Clauses or equivalent transfer
        mechanisms to protect your data.
      </p>

      <h2>6. Data Retention</h2>
      <ul>
        <li>Account data is retained while your account is active</li>
        <li>
          Dosha results and Ask Donny conversations are retained for
          continuity while your account is active
        </li>
        <li>
          Support-form submissions are retained for as long as necessary
          to answer your inquiry and comply with legal obligations
        </li>
        <li>Aggregated, non-identifying analytics may be retained longer</li>
      </ul>

      <h2>7. Your Rights</h2>
      <p>
        Depending on your jurisdiction, you may have the right to
        access, correct, delete, or export your personal information,
        restrict or object to processing, and withdraw consent where
        applicable. To exercise any of these rights, contact us through
        the <Link to="/support">Support page</Link>.
      </p>

      <h2>8. Account and Data Deletion</h2>
      <p>
        To request deletion of your DONiTSH!T account and associated
        personal data, submit a request through our{" "}
        <Link to="/support">Support page</Link> using the topic "Account
        deletion." A team member will confirm the request and remove
        your account, saved Dosha history, Ask Donny conversations,
        saved practitioners, and business listings from active systems
        within a reasonable timeframe. Some information may be retained
        where required by law (for example, transactional records).
      </p>

      <h2>9. Cookies</h2>
      <p>
        We use one functional cookie, <code>session_token</code>, solely
        to keep you signed in. At the time of publication we do not
        deploy advertising cookies or third-party analytics cookies on
        the website.
      </p>

      <h2>10. Children</h2>
      <p>
        The Service is not directed at children under 13. If you
        believe a child has provided us with personal information,
        contact us through the Support page and we will remove it.
      </p>

      <h2>11. Changes</h2>
      <p>
        We may update this Privacy Policy from time to time. The
        "Last updated" date at the top reflects the most recent
        revision.
      </p>

      <h2>12. Contact</h2>
      <p>
        Reach us through the <Link to="/support">Support page</Link>{" "}
        contact form (topic: "Privacy"). This is the current channel
        for all privacy inquiries, rights requests, and data-deletion
        requests.
      </p>
    </LegalPageLayout>
  );
}
